import { DateOnly } from "client/DateAndTime/dateOnly";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import { never } from "client_legacy/ndaw/errors";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";
import { NormalizedSet } from "client_legacy/ndaw/normalizedSet";

export function deepComparison<TValue>(objA: TValue, objB: TValue): boolean {
	return compareInternal(
		objA,
		objB,
		new Set<unknown>(),
		new Set<unknown>(),
		"deep"
	);
}

export function shallowComparison<TValue>(objA: TValue, objB: TValue): boolean {
	return compareInternal(
		objA,
		objB,
		new Set<unknown>(),
		new Set<unknown>(),
		"shallow"
	);
}

function compareInternal(
	objA: unknown,
	objB: unknown,
	stackA: Set<unknown>,
	stackB: Set<unknown>,
	mode: "deep" | "shallow"
): boolean {
	if (stackA.has(objA)) {
		throw new Error(`Cyclic reference detected. Object ${objA} is its own ancestor.`);
	}

	if (stackB.has(objB)) {
		throw new Error(`Cyclic reference detected. Object ${objB} is its own ancestor.`);
	}

	stackA.add(objA);
	stackB.add(objB);

	try {
		if (Object.is(objA, objB)) {
			return true;
		}

		if (typeof objA !== "object" || objA === null || typeof objB !== "object" || objB === null) {
			return false;
		}

		const compareChildren = (childA: unknown, childB: unknown): boolean => {
			switch (mode) {
				case "deep":
					return compareInternal(
						childA,
						childB,
						stackA,
						stackB,
						"deep"
					);
				case "shallow":
					return Object.is(childA, childB);
				default:
					never(mode);
			}
		};

		if (objA instanceof Set && objB instanceof Set) {
			let arrayB: unknown[] | undefined = undefined;

			const doesSetBContainElement = (setB: Set<unknown>, childA: unknown): boolean => {
				switch (mode) {
					case "deep":
						if (setB.has(childA)) {
							return true;
						}

						if (!arrayB) {
							arrayB = Array.from(objB);
						}

						return arrayB.some(childB => compareChildren(childA, childB));
					case "shallow":
						return setB.has(childA);
					default:
						never(mode);
				}
			};

			return compareSets(objA, objB, doesSetBContainElement);
		} else if (objA instanceof Set || objB instanceof Set) {
			return false;
		}

		if (objA instanceof NormalizedSet && objB instanceof NormalizedSet) {
			let arrayB: unknown[] | undefined = undefined;

			const doesSetBContainElement = (setB: Set<unknown>, childA: unknown): boolean => {
				switch (mode) {
					case "deep":
						if (setB.has(childA)) {
							return true;
						}

						if (!arrayB) {
							arrayB = Array.from(objB);
						}

						return arrayB.some(childB => compareChildren(childA, childB));
					case "shallow":
						return setB.has(childA);
					default:
						never(mode);
				}
			};

			return compareSets(objA, objB, doesSetBContainElement);
		} else if (objA instanceof NormalizedSet || objB instanceof NormalizedSet) {
			return false;
		}

		if (objA instanceof Map && objB instanceof Map) {
			return compareMaps(objA, objB, compareChildren);
		} else if (objA instanceof Map || objB instanceof Map) {
			return false;
		}

		if (objA instanceof NormalizedMap && objB instanceof NormalizedMap) {
			return compareMaps(objA, objB, compareChildren);
		} else if (objA instanceof NormalizedMap || objB instanceof NormalizedMap) {
			return false;
		}

		if (Array.isArray(objA) && Array.isArray(objB)) {
			return compareArrays(objA, objB, compareChildren);
		} else if (Array.isArray(objA) || Array.isArray(objB)) {
			return false;
		}

		if (objA instanceof Date && objB instanceof Date) {
			return compareDates(objA, objB);
		} else if (objA instanceof Date || objB instanceof Date) {
			return false;
		}

		if (objA instanceof DateOnly && objB instanceof DateOnly) {
			return compareDateOnlys(objA, objB);
		} else if (objA instanceof DateOnly || objB instanceof DateOnly) {
			return false;
		}

		if (objA instanceof TimeOnly && objB instanceof TimeOnly) {
			return compareTimeOnlys(objA, objB);
		} else if (objA instanceof TimeOnly || objB instanceof TimeOnly) {
			return false;
		}

		return compareObjects(objA, objB, compareChildren);
	} finally {
		stackA.delete(objA);
		stackB.delete(objB);
	}
}

function compareSets(
	setA: Set<unknown>,
	setB: Set<unknown>,
	doesSetBContainElement: (setB: Set<unknown>, childA: unknown) => boolean): boolean {
	if (setA.size !== setB.size) {
		return false;
	}

	for (const childA of setA) {
		if (!doesSetBContainElement(setB, childA)) {
			return false;
		}
	}

	return true;
}

function compareMaps(
	mapA: Map<unknown, unknown>,
	mapB: Map<unknown, unknown>,
	compareChildren: (childA: unknown, childB: unknown) => boolean): boolean {
	if (mapA.size !== mapB.size) {
		return false;
	}

	for (const keyA of mapA.keys()) {
		if (!mapB.has(keyA)) {
			return false;
		}

		const childA: unknown = mapA.get(keyA);
		const childB: unknown = mapB.get(keyA);

		if (!compareChildren(childA, childB)) {
			return false;
		}
	}

	return true;
}

function compareArrays(
	arrayA: readonly unknown[],
	arrayB: readonly unknown[],
	compareChildren: (childA: unknown, childB: unknown) => boolean): boolean {
	if (arrayA.length !== arrayB.length) {
		return false;
	}

	for (let i = 0; i < arrayA.length; i++) {
		const childA: unknown = arrayA[i];
		const childB: unknown = arrayB[i];

		if (!compareChildren(childA, childB)) {
			return false;
		}
	}

	return true;
}

function compareDates(
	dateA: Date,
	dateB: Date): boolean {
	const timeA: number = new Date(dateA).getTime();
	const timeB: number = new Date(dateB).getTime();

	return timeA === timeB;
}

function compareDateOnlys(
	dateA: DateOnly,
	dateB: DateOnly
): boolean {
	return dateA.isEqualTo(dateB);
}

function compareTimeOnlys(
	timeA: TimeOnly,
	timeB: TimeOnly
): boolean {
	return timeA.isEqualTo(timeB);
}

function compareObjects(
	objA: {},
	objB: {},
	compareChildren: (childA: unknown, childB: unknown) => boolean
): boolean {
	const keysA: string[] = Object.keys(objA);
	if (keysA.length !== Object.keys(objB).length) {
		return false;
	}

	for (let i = 0; i < keysA.length; i++) {
		if (!Object.prototype.hasOwnProperty.call(objB, keysA[i])) {
			return false;
		}

		const key = keysA[i] as keyof typeof objA;

		if (!compareChildren(objA[key], objB[key])) {
			return false;
		}
	}

	return true;
}
